import { useEffect, useState } from "react";
import { Heading } from "../components/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { collection, onSnapshot, query, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import EmptyState from "./components/empty-state";
import { QuestionMarkCircleIcon } from "@heroicons/react/20/solid";
import { BSGroom } from "@/models/groom";
import { BSPayrollItem } from "@/models/payrollItem";
import { formatCurrency } from "../helpers/currency.helpers";

interface WeeklySummary {
  week1: number;
  week2: number;
  week3: number;
  week4: number;
}

interface GroomSummary extends WeeklySummary {
  groomId: string;
  name: string;
}

export default function PayrollSummary() {
  const [grooms, setGrooms] = useState<BSGroom[]>([]);
  const [payrollItems, setPayrollItems] = useState<BSPayrollItem[]>([]);
  const [summary, setSummary] = useState<GroomSummary[]>([]);
  const [totals, setTotals] = useState<WeeklySummary>({ week1: 0, week2: 0, week3: 0, week4: 0 });
  const [weekDates, setWeekDates] = useState<string[]>([]);

  // Fetch data
  useEffect(() => {
    const fetchGrooms = () => {
      const q = query(collection(db, "grooms"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSGroom[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ ...doc.data() as BSGroom, id: doc.id });
        });
        setGrooms(items);
      });
    };

    const fetchPayrollItems = () => {
      const q = query(collection(db, "payroll"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSPayrollItem[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as BSPayrollItem;
          const date = data.date instanceof Timestamp
            ? data.date.toDate()
            : data.date;
          items.push({ ...data, id: doc.id, date });
        });
        setPayrollItems(items);
      });
    };

    fetchGrooms();
    fetchPayrollItems();
  }, []);

  // Calculate summaries when data changes
  useEffect(() => {
    const currentDate = new Date();
    const fourWeeksAgo = new Date(currentDate);
    fourWeeksAgo.setDate(currentDate.getDate() - 28);

    // Get Monday of current week
    const getMonday = (d: Date) => {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      return new Date(d.setDate(diff));
    };

    const currentMonday = getMonday(new Date(currentDate));
    currentMonday.setHours(0, 0, 0, 0);  // Start of day

    const weekBoundaries = [
      new Date(currentMonday.getTime() - (7 * 24 * 60 * 60 * 1000 * 3)), // 3 weeks ago
      new Date(currentMonday.getTime() - (7 * 24 * 60 * 60 * 1000 * 2)), // 2 weeks ago
      new Date(currentMonday.getTime() - (7 * 24 * 60 * 60 * 1000)),     // 1 week ago
      currentMonday,                                                      // this week
      new Date(currentMonday.getTime() + (7 * 24 * 60 * 60 * 1000))      // next week
    ];

    // Set all boundaries to start of day
    weekBoundaries.forEach(date => date.setHours(0, 0, 0, 0));

    // Format dates for headers
    const formattedDates = weekBoundaries.slice(0, -1).map(date => 
      date.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      })
    );
    setWeekDates(formattedDates);

    // Calculate summary for each groom
    const groomSummaries = grooms.map(groom => {
      const groomItems = payrollItems.filter(item => item.groomId === groom.id);
      
      const calculateWeekTotal = (start: Date, end: Date) => {
        return groomItems
          .filter(item => item.date >= start && item.date < end)
          .reduce((sum, item) => sum + Number(item.value || 0), 0);
      };
      
      return {
        groomId: groom.id,
        name: groom.name,
        week1: calculateWeekTotal(weekBoundaries[0], weekBoundaries[1]),
        week2: calculateWeekTotal(weekBoundaries[1], weekBoundaries[2]),
        week3: calculateWeekTotal(weekBoundaries[2], weekBoundaries[3]),
        week4: calculateWeekTotal(weekBoundaries[3], weekBoundaries[4]),
      };
    });

    // Calculate totals
    const weekTotals = {
      week1: groomSummaries.reduce((sum, groom) => sum + Number(groom.week1 || 0), 0),
      week2: groomSummaries.reduce((sum, groom) => sum + Number(groom.week2 || 0), 0),
      week3: groomSummaries.reduce((sum, groom) => sum + Number(groom.week3 || 0), 0),
      week4: groomSummaries.reduce((sum, groom) => sum + Number(groom.week4 || 0), 0),
    };

    // Round all numbers to 2 decimal places
    const roundedTotals = {
      week1: Math.round(weekTotals.week1 * 100) / 100,
      week2: Math.round(weekTotals.week2 * 100) / 100,
      week3: Math.round(weekTotals.week3 * 100) / 100,
      week4: Math.round(weekTotals.week4 * 100) / 100,
    };

    setSummary(groomSummaries);
    setTotals(roundedTotals);
  }, [grooms, payrollItems]);

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Payroll Summary</Heading>
      </div>
      {summary.length > 0 ? (
        <Table
          dense
          striped
          className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
        >
          <TableHead>
            <TableRow>
              <TableHeader>Name on Check</TableHeader>
              {weekDates.map((date, index) => (
                <TableHeader key={date}>{date}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.map((row) => (
              <TableRow key={row.groomId}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{formatCurrency(row.week1)}</TableCell>
                <TableCell>{formatCurrency(row.week2)}</TableCell>
                <TableCell>{formatCurrency(row.week3)}</TableCell>
                <TableCell>{formatCurrency(row.week4)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="font-bold bg-zinc-100 dark:bg-zinc-800">
              <TableCell>Grand Total</TableCell>
              <TableCell>{formatCurrency(totals.week1)}</TableCell>
              <TableCell>{formatCurrency(totals.week2)}</TableCell>
              <TableCell>{formatCurrency(totals.week3)}</TableCell>
              <TableCell>{formatCurrency(totals.week4)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          title="No Data"
          description="No payroll data to display"
        >
          <QuestionMarkCircleIcon className="w-10 h-10" />
        </EmptyState>
      )}
    </div>
  );
}
